import React, { useState, useEffect } from 'react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import {
  Box,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Table,
  Button,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  VStack,
  Text,
  Container,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase-config';

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleDateString();
};

const safeStringify = (value) => {
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return value;
};

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // User stats
  const [userStats, setUserStats] = useState(null);
  
  // Memory stats
  const [memoryStats, setMemoryStats] = useState(null);
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const { isAdmin } = useAuth();

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      setError('');
      const getAdminUserStats = httpsCallable(functions, 'getAdminUserStats');
      const { data } = await getAdminUserStats();
      
      setUserStats({
        totalUsers: data.totalUsers,
        activeUsers: data.activeUsers,
        users: data.users.map(user => ({
          ...user,
          lastLogin: formatDate(user.lastLogin),
          createdAt: formatDate(user.createdAt)
        })),
        lastUpdated: data.lastUpdated
      });
    } catch (err) {
      console.error('Error fetching user stats:', err);
      setError('Failed to load user stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchMemoryStats = async () => {
    try {
      setLoading(true);
      setError('');
      const getAdminMemoryStats = httpsCallable(functions, 'getAdminMemoryStats');
      const { data } = await getAdminMemoryStats({ page: currentPage, itemsPerPage });
      
      setMemoryStats(data);
    } catch (err) {
      console.error('Error fetching memory stats:', err);
      setError('Failed to load memory stats');
    } finally {
      setLoading(false);
    }
  };

  const openInGoogleSheets = () => {
    if (!memoryStats?.memories) return;

    // Convert the data to CSV format
    const headers = ['Timestamp', 'Subject', 'From', 'Content', 'Intent', 'Emotion', 'Strategy', 'Needs', 'Response Subject', 'Response Body', 'Status'];
    const rows = memoryStats.memories.map(analysis => [
      new Date(analysis.execution.timestamp).toLocaleString(),
      analysis.input.subject,
      analysis.input.from,
      analysis.input.content,
      JSON.stringify(analysis.processing.intent),
      JSON.stringify(analysis.processing.emotional_context),
      JSON.stringify(analysis.processing.response_strategy),
      Array.isArray(analysis.processing.context_needs) ? analysis.processing.context_needs.join(', ') : JSON.stringify(analysis.processing.context_needs),
      analysis.response.subject,
      analysis.response.body,
      analysis.execution.success ? 'Success' : 'Failed'
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // Create a Base64 encoded CSV string
    const base64CSV = btoa(unescape(encodeURIComponent(csvContent)));

    // Open in Google Sheets
    const googleSheetsUrl = `https://docs.google.com/spreadsheets/create?data=${base64CSV}`;
    window.open(googleSheetsUrl, '_blank');
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedMemories = () => {
    if (!memoryStats?.memories) return [];
    
    return [...memoryStats.memories].sort((a, b) => {
      const aValue = sortField.split('.').reduce((obj, key) => obj?.[key], a);
      const bValue = sortField.split('.').reduce((obj, key) => obj?.[key], b);
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  };

  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab === 0) {
      fetchUserStats();
    } else {
      fetchMemoryStats();
    }
  }, [isAdmin, activeTab, currentPage]);

  if (!isAdmin) {
    return (
      <Container maxW="container.xl" py={5}>
        <Text>You do not have access to this page.</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={5}>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>Admin Dashboard</Heading>
          {error && <Text color="red.500">{error}</Text>}
        </Box>

        <Tabs onChange={setActiveTab} isLazy>
          <TabList>
            <Tab>User Stats</Tab>
            <Tab>Quality Analysis</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              {loading ? (
                <Spinner />
              ) : userStats && (
                <VStack spacing={8} align="stretch">
                  <StatGroup>
                    <Stat>
                      <StatLabel>Total Users</StatLabel>
                      <StatNumber>{userStats.totalUsers}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Active Users (30d)</StatLabel>
                      <StatNumber>{userStats.activeUsers}</StatNumber>
                      <Text fontSize="sm" color="gray.500">
                        Last updated: {new Date(userStats.lastUpdated).toLocaleString()}
                      </Text>
                    </Stat>
                  </StatGroup>

                  <Box>
                    <Heading size="md" mb={4}>User List</Heading>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Email</Th>
                          <Th>Display Name</Th>
                          <Th>Last Login</Th>
                          <Th>Created At</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {userStats.users.map(user => (
                          <Tr key={user.id}>
                            <Td>{user.email}</Td>
                            <Td>{user.displayName}</Td>
                            <Td>{user.lastLogin}</Td>
                            <Td>{user.createdAt}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </VStack>
              )}
            </TabPanel>

            <TabPanel>
              {loading ? (
                <Spinner />
              ) : memoryStats && (
                <VStack spacing={8} align="stretch">
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <StatGroup>
                      <Stat>
                        <StatLabel>Total Analyses</StatLabel>
                        <StatNumber>{memoryStats.summary.totalMemories}</StatNumber>
                      </Stat>
                    </StatGroup>
                    <Button
                      leftIcon={<ExternalLinkIcon />}
                      colorScheme="blue"
                      onClick={openInGoogleSheets}
                      size="sm"
                    >
                      Open in Google Sheets
                    </Button>
                  </Box>

                  <Box>
                    <Heading size="md" mb={4}>Analysis List</Heading>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th width="150px" cursor="pointer" onClick={() => handleSort('execution.timestamp')}>
                            Timestamp {sortField === 'execution.timestamp' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </Th>
                          <Th width="200px">Input</Th>
                          <Th width="150px" cursor="pointer" onClick={() => handleSort('processing.intent')}>
                            Intent {sortField === 'processing.intent' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </Th>
                          <Th width="150px">Context</Th>
                          <Th width="200px">Response</Th>
                          <Th width="150px">Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {getSortedMemories().map(analysis => (
                          <Tr key={analysis.id}>
                            <Td width="150px">{new Date(analysis.execution.timestamp).toLocaleString()}</Td>
                            <Td width="200px">
                              <Text><strong>Subject:</strong> {safeStringify(analysis.input.subject)}</Text>
                              <Text mt={2}><strong>From:</strong> {safeStringify(analysis.input.from)}</Text>
                              <Text mt={2}><strong>Content:</strong> {safeStringify(analysis.input.content)}</Text>
                            </Td>
                            <Td width="150px">
                              <Text><strong>Intent:</strong> {safeStringify(analysis.processing.intent)}</Text>
                              <Text mt={2}><strong>Emotion:</strong> {safeStringify(analysis.processing.emotional_context)}</Text>
                              <Text mt={2}><strong>Strategy:</strong> {safeStringify(analysis.processing.response_strategy)}</Text>
                            </Td>
                            <Td width="150px">
                              <Text><strong>Needs:</strong> {Array.isArray(analysis.processing.context_needs) ? analysis.processing.context_needs.join(', ') : safeStringify(analysis.processing.context_needs)}</Text>
                              <Text mt={2}><strong>Count:</strong> {safeStringify(analysis.processing.context_count)}</Text>
                            </Td>
                            <Td width="200px">
                              <Text><strong>Subject:</strong> {safeStringify(analysis.response.subject)}</Text>
                              <Text mt={2}><strong>Body:</strong> {safeStringify(analysis.response.body)}</Text>
                            </Td>
                            <Td width="150px">
                              <Text color={analysis.execution.success ? 'green.500' : 'red.500'}>
                                {analysis.execution.success ? 'Success' : 'Failed'}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </VStack>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Container>
  );
}
